import { useState, useEffect, useRef } from "react";
import { DomEvent } from "leaflet";
import { Link } from "react-router-dom";
import classes from "./ArmiesBox.module.scss";
import CloseButton from "../buttons/CloseButton";
import ArmyForm from "../forms/ArmyForm";
import MultipleArmiesForm from "../forms/MultipleArmiesForm";
import Tippy from "@tippyjs/react";

const ArmiesBox = ({ armies, filteredArmies, setFilteredArmies, nodes, onClose, setCenter, disabled }) => {
  const groupArmiesByFaction = (armies) => {
    return Object.entries(
      armies.reduce((acc, army) => {
        if (!acc[army.faction]) {
          acc[army.faction] = [];
        }
        acc[army.faction].push(army);
        return acc;
      }, {})
    )
      .sort(([factionA], [factionB]) => {
        if (factionA.startsWith('bl')) return -1;
        if (factionB.startsWith('bl')) return 1;
        return 0;
      })
      .map(([faction, factionArmies]) => [
        faction,
        factionArmies.sort((a, b) => a.name.localeCompare(b.name)) // Sort armies alphabetically within faction
      ])
      .filter(([factionName]) => factionName !== 'scout'); // Exclude scouts
  };


  const [creatingArmy, setCreatingArmy] = useState(false);
  const [creatingMultipleArmies, setCreatingMultipleArmies] = useState(false);
  const [editingArmy, setEditingArmy] = useState(null);
  const [filteredArmiesByFaction, setFilteredArmiesByFaction] = useState(groupArmiesByFaction(filteredArmies));

  const armiesByFaction = groupArmiesByFaction(armies);
  const armiesWithoutNode = armies
    .filter((army) => !army.node)
    .sort((a, b) => a.name.localeCompare(b.name));

  const containerRef = useRef(null);
  const armiesWithoutNodeRef = useRef(null);
  useEffect(() => {
    if (containerRef.current) {
      DomEvent.disableScrollPropagation(containerRef.current);
    }
  }, [containerRef]);

  const handleClick = (army) => {
    const armyPosition = nodes.find((node) => node.id === army.node)?.position;
    if (!armyPosition) return;

    setCenter(null);
    setTimeout(() => setCenter(armyPosition), 0);
  };

  const handleArmiesWithoutNodeClick = () => {
    armiesWithoutNodeRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleFilterClick = (faction, factionArmies) => {
    if (filteredArmiesByFaction.some(([factionName]) => factionName === faction)) {
      const updatedFilteredArmiesByFaction = filteredArmiesByFaction.filter(([factionName]) => factionName !== faction);
      setFilteredArmiesByFaction(updatedFilteredArmiesByFaction);
      const updatedFilteredArmies = filteredArmies.filter((army) => army.faction !== faction);
      setFilteredArmies(updatedFilteredArmies);
    } else {
      const updatedFilteredArmiesByFaction = [...filteredArmiesByFaction, [faction, factionArmies]];
      setFilteredArmiesByFaction(updatedFilteredArmiesByFaction);
      const armiesToAdd = armies.filter((army) => army.faction === faction);
      setFilteredArmies([...filteredArmies, ...armiesToAdd]);
    }
  };

  if (creatingArmy) {
    return <ArmyForm nodes={nodes} onClose={onClose} disabled={disabled} />;
  }

  if (editingArmy) {
    return <ArmyForm army={editingArmy} nodes={nodes} onClose={onClose} disabled={disabled} />;
  }

  if (creatingMultipleArmies) {
    return <MultipleArmiesForm onClose={onClose} disabled={disabled} />;
  }

  return (
    <div className={classes.box} ref={containerRef}>
      <CloseButton onClose={onClose} />
      {!disabled &&
        <div className={classes.actions}>
          <div onClick={() => setCreatingArmy(true)} className={classes.btn}>
            Add new army
          </div>
          <div onClick={() => setCreatingMultipleArmies(true)} className={classes.btn}>
            Add multiple armies
          </div>
        </div>}
      <div className={classes.filtersBox}>
        {armiesByFaction.map(([faction, factionArmies], index) => {
          const factionExists = filteredArmiesByFaction.some(([factionName]) => factionName === faction);
          return (
            <div key={index} className={classes.filterItem}>
              <Tippy content={faction} placement="top" arrow={false} offset={[0, 1]} theme="small">
                <img
                  src={`/Images/flags/${faction}.png`}
                  alt=""
                  className={
                    factionExists
                      ? classes.flagFilter
                      : `${classes.flagFilter} ${classes.dimmed}`
                  }
                  onClick={() => handleFilterClick(faction, factionArmies)}
                />
              </Tippy>
            </div>
          );
        })}
        <Tippy content="Armies without node" placement="top" arrow={false} offset={[0, 1]} theme="small">
          <button
            type="button"
            className={classes.noNodeButton}
            onClick={handleArmiesWithoutNodeClick}
          >
            Unplaced Armies ({armiesWithoutNode.length})
          </button>
        </Tippy>
      </div>

      <h5>Armies:</h5>

      {filteredArmiesByFaction.map(([faction, factionArmies], index) => (
        <div key={`wrapper-${faction}`}>
          {index !== 0 && <div className={classes.horizontalLine}></div>}
          <div key={faction}>
            <img
              src={`/Images/flags/${faction}.png`}
              className={`${classes.flagInList}`}
            />
            <ul>
              {factionArmies.map(army => (
                <li key={`army-${army.id}`}>
                  <Link onClick={() => handleClick(army)}>
                    {army.name} - node {army.node}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
      <div ref={armiesWithoutNodeRef} className={classes.armiesWithoutNode}>
        <div className={classes.horizontalLine}></div>
        <h5>Unplaced armies ({armiesWithoutNode.length}):</h5>
        {armiesWithoutNode.length > 0 && (
          <ul>
            {armiesWithoutNode.map((army) => (
              <li key={`army-without-node-${army.id}`}>
                <Link onClick={() => setEditingArmy(army)}>
                  {army.name}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ArmiesBox;
